const parseHashSearchParams = (hash: string): URLSearchParams => {
  if (!hash || hash.length <= 1) {
    return new URLSearchParams();
  }

  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryIndex = raw.indexOf('?');
  const queryString = queryIndex >= 0 ? raw.slice(queryIndex + 1) : raw;

  return new URLSearchParams(queryString);
};

const extractFragmentValue = ({ hash, name }: { hash: string; name: string }): string | null => {
  if (!hash || hash.length <= 1) {
    return null;
  }

  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const fragment = raw.includes('?') ? raw.slice(raw.indexOf('?') + 1) : raw;
  const segments = fragment.split('&');
  const prefix = `${name}=`;

  for (const segment of segments) {
    if (!segment.startsWith(prefix)) {
      continue;
    }

    const value = segment.slice(prefix.length);
    if (!value.trim()) {
      return null;
    }

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  return null;
};

const FRAGMENT_FIELD_NAMES = [
  'access_token',
  'id_token',
  'refresh_token',
  'expires_in',
  'token_type',
  'scope',
] as const;

type FragmentFieldName = (typeof FRAGMENT_FIELD_NAMES)[number];

const readFragmentField = ({
  name,
  searchParams,
  hashParams,
  hash,
}: {
  name: FragmentFieldName;
  searchParams: URLSearchParams;
  hashParams: URLSearchParams;
  hash: string;
}): string | null => {
  const fromSearch = searchParams.get(name)?.trim();
  if (fromSearch) {
    return fromSearch;
  }

  const fromHashParams = hashParams.get(name)?.trim();
  if (fromHashParams) {
    return fromHashParams;
  }

  return extractFragmentValue({ hash, name });
};

const parseExpiresIn = (rawExpiresIn: string | null): number | null => {
  if (!rawExpiresIn) {
    return null;
  }

  const parsed = parseInt(rawExpiresIn, 10);
  if (Number.isNaN(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};

type LocationLike = {
  search: string;
  hash: string;
};

export type SuperadminHandoffParams = {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  expiresIn: number | null;
  tokenType: string | null;
  scope: string | null;
  rawState: string | null;
};

export const getSuperadminHandoffParamsFromLocation = ({ search, hash }: LocationLike): SuperadminHandoffParams => {
  const searchParams = new URLSearchParams(search);
  const hashParams = parseHashSearchParams(hash);

  const accessToken = readFragmentField({ name: 'access_token', searchParams, hashParams, hash });
  const idToken = readFragmentField({ name: 'id_token', searchParams, hashParams, hash });
  const refreshToken = readFragmentField({ name: 'refresh_token', searchParams, hashParams, hash });
  const expiresIn = parseExpiresIn(readFragmentField({ name: 'expires_in', searchParams, hashParams, hash }));
  const tokenType = readFragmentField({ name: 'token_type', searchParams, hashParams, hash });
  const scope = readFragmentField({ name: 'scope', searchParams, hashParams, hash });
  const rawState = searchParams.get('state')?.trim() || hashParams.get('state')?.trim() || null;

  return {
    accessToken,
    idToken,
    refreshToken,
    expiresIn,
    tokenType,
    scope,
    rawState,
  };
};

const parseStateJson = (rawState: string): Record<string, unknown> | null => {
  try {
    const decoded = decodeURIComponent(rawState);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    try {
      return JSON.parse(rawState) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
};

export const buildImpersonationRedirectStateFromParsedJson = (
  parsedState: Record<string, unknown>
): Record<string, string> | null => {
  const rawImpersonatedUserEmail = parsedState['X-Impersonate-User'];
  if (typeof rawImpersonatedUserEmail !== 'string' || !rawImpersonatedUserEmail.trim()) {
    return null;
  }

  const redirectState: Record<string, string> = {};

  redirectState['X-Impersonate-User'] = rawImpersonatedUserEmail.trim();

  const impersonatedUserName = parsedState.impersonatedUserName;
  if (typeof impersonatedUserName === 'string' && impersonatedUserName.trim()) {
    redirectState.impersonatedUserName = impersonatedUserName.trim();
  }

  const returnToControlCenterUrl = parsedState.returnToControlCenterUrl;
  if (typeof returnToControlCenterUrl === 'string' && returnToControlCenterUrl.trim()) {
    redirectState.returnToControlCenterUrl = returnToControlCenterUrl.trim();
  }

  const churchId = parsedState.church_id;
  if (typeof churchId === 'string' && churchId.trim()) {
    redirectState.church_id = churchId.trim();
  }

  const isOverallAdmin = parsedState.isOverallAdmin;
  if (isOverallAdmin === 'true' || isOverallAdmin === true) {
    redirectState.isOverallAdmin = 'true';
  }

  if (Object.keys(redirectState).length === 0) {
    return null;
  }

  return redirectState;
};

export const parseImpersonationStateFromRawStateParam = (rawState: string | null): Record<string, string> | null => {
  if (!rawState?.trim()) {
    return null;
  }

  const parsedState = parseStateJson(rawState.trim());
  if (!parsedState) {
    return null;
  }

  return buildImpersonationRedirectStateFromParsedJson(parsedState);
};

export const stripSuperadminHandoffFromBrowserUrl = ({
  churchId: churchIdOverride,
}: { churchId?: string | null } = {}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  const churchIdFromQuery = url.searchParams.get('church_id');

  FRAGMENT_FIELD_NAMES.forEach((fieldName) => url.searchParams.delete(fieldName));
  url.searchParams.delete('state');

  const nextSearch = new URLSearchParams();
  const churchIdToKeep = churchIdOverride?.trim() || churchIdFromQuery?.trim();
  if (churchIdToKeep) {
    nextSearch.set('church_id', churchIdToKeep);
  }

  const searchPart = nextSearch.toString() ? `?${nextSearch.toString()}` : '';
  url.hash = '';

  window.history.replaceState({}, document.title, `${url.pathname}${searchPart}`);
};
